// Turning an ONVIF camera's reported capabilities into a video configuration.
//
// WHY THIS EXISTS. An ONVIF camera does not serve video at a guessable address.
// The Bolin at 192.168.0.73 answers on
// `rtsp://…:554/media/video1&t=unicast&p=udp&ve=H265&w=1920&h=1080&ae=MP4A-LATM`
// — a path and a query string nothing can invent. The wizard composed
// `rtsp://host` with an empty path instead, so every ONVIF camera failed both
// checks with "did not connect in time", which no password could fix
// (reproduced live 2026-08-25).
//
// The production Add Video Source flow never composes that URL. It adds a
// TRANSIENT ONVIF control device, waits for the bridge to report that device's
// capabilities, and reads the stream URI off them
// (video/components/add/source/discovery.tsx: "wait for the transient ONVIF
// control device to show up in the preview, and query its capabilities for the
// RTSP video URI"). This module is the same translation, extracted so the wizard
// calls it rather than growing a third interpretation — and so it can be tested
// without a camera.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments, investigate-before-adding (ai.knowledge/global).

import { toConfiguration } from '@studio/models/video';
import { defaults } from 'lodash-es';

/** The credentials half, as the panel passes them. */
export interface OnvifCredentials {
  readonly username: string;
  readonly password: string;
}

/**
 * The video configuration for an ONVIF camera, or undefined when its
 * capabilities do not name a stream yet.
 *
 * Deliberately the SAME expression the panel uses
 * (`toConfiguration(defaults({}, uri, credentials))`): the URI object supplies
 * host, port, path and query, and the credentials are layered in without
 * overwriting anything the camera reported. Diverging by even a field means the
 * wizard adds a camera the panel would have added differently, which is the
 * class of bug discovery-parity.test.ts exists to catch.
 *
 * Undefined is a real answer, not a failure: capabilities arrive before the
 * discovery block is populated, so the caller waits rather than adding a device
 * with no stream.
 */
export const onvifVideoSettings = (
  capabilities: { readonly discovery?: { readonly uri?: unknown } | undefined } | undefined,
  credentials?: OnvifCredentials | undefined
): unknown => {
  const uri = capabilities?.discovery?.uri;
  if (uri === undefined) return undefined;

  // A blank username is "no credentials", matching the manual form, which sends
  // empty strings for a camera that needs none.
  // Merged at the TOP level, not under a `credentials` key: toConfiguration
  // takes a URI-shaped object (host, protocol, username, password), which is the
  // shape the panel hands it.
  const withCredentials =
    credentials === undefined || credentials.username.trim() === ''
      ? undefined
      : { username: credentials.username.trim(), password: credentials.password };

  return toConfiguration(defaults({}, uri, withCredentials) as never);
};

/**
 * The ONVIF control configuration to probe with, with credentials attached.
 *
 * The probe device is control-only on purpose: it exists to make the camera
 * answer, and asking it for video before it has told us the stream URI is the
 * mistake this whole path replaces.
 */
export const onvifProbeControl = (
  control: unknown,
  credentials?: OnvifCredentials | undefined
): unknown =>
  credentials === undefined || credentials.username.trim() === ''
    ? control
    : {
        ...(control as object),
        credentials: { username: credentials.username.trim(), password: credentials.password }
      };
